import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../maps/data.js";

function ReloadPlanograms() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stores, setStores] = useState([]);
  const [clients, setClients] = useState([]);
  const [edges, setEdges] = useState([]);

  const [selectedStore, setSelectedStore] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedEdge, setSelectedEdge] = useState("");
  const [fieldsData, setFieldsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedShelves, setSelectedShelves] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api({ type: "getClients" });
        setClients(res);
      } catch (err) {
        console.error("Failed to fetch clients:", err);
        setError("Unable to fetch clients. Please try again later.");
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const clientId = searchParams.get("client");
    const storeId = searchParams.get("store");
    const edgeId = searchParams.get("edge");

    if (clientId) {
      setSelectedClient(clientId);
      const savedStores = localStorage.getItem(`stores_${clientId}`);
      if (savedStores) {
        setStores(JSON.parse(savedStores));
        if (storeId) {
          setSelectedStore(storeId);
        }
      } else {
        fetchStores(clientId);
      }

      if (storeId) {
        const savedEdges = localStorage.getItem(`edges_${storeId}`);
        if (savedEdges) {
          setEdges(JSON.parse(savedEdges));
          if (edgeId) {
            setSelectedEdge(edgeId);
          }
        } else {
          fetchEdges(storeId);
        }
      }
    }
  }, [searchParams]);

  const fetchStores = async (clientId) => {
    try {
      const res = await api({ type: "getStores", data: { clientId } });
      setStores(res);
      localStorage.setItem(`stores_${clientId}`, JSON.stringify(res));
    } catch (err) {
      console.error("Failed to fetch stores:", err);
      setError("Unable to fetch stores. Please try again later.");
    }
  };

  const fetchEdges = async (siteId) => {
    try {
      const res = await api({ type: "getEdges", data: { siteId } });
      setEdges(res);
      localStorage.setItem(`edges_${siteId}`, JSON.stringify(res));
    } catch (err) {
      console.error("Failed to fetch edges:", err);
      setError("Unable to fetch edges. Please try again later.");
    }
  };

  useEffect(() => {
    if (selectedEdge) {
      setLoading(true);
      setError(null);
      navigate(
        `?client=${selectedClient}&store=${selectedStore}&edge=${selectedEdge}`
      );

      const fetchFields = async () => {
        try {
          const res = await api({
            type: "getFields",
            data: { edgeId: selectedEdge },
          });
          setFieldsData(res);
        } catch (err) {
          console.error("Failed to fetch fields data:", err);
          setError("Unable to fetch fields data. Please try again later.");
        } finally {
          setLoading(false);
        }
      };

      fetchFields();
    }
  }, [selectedEdge, selectedClient, selectedStore]);

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setSelectedStore("");
    setSelectedEdge("");
    setFieldsData(null);
    setStores([]);
    setEdges([]);
    navigate(`?client=${clientId}`);
    fetchStores(clientId);
  };

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    setSelectedEdge("");
    setFieldsData(null);
    setEdges([]);
    navigate(`?client=${selectedClient}&store=${storeId}`);
    fetchEdges(storeId);
  };

  const handleEdgeChange = (edgeId) => {
    setSelectedEdge(edgeId);
    setFieldsData(null);
    setError(null);
    setLoading(true);
    navigate(`?client=${selectedClient}&store=${selectedStore}&edge=${edgeId}`);
  };

  const handleClearAll = () => {
    setSelectedClient("");
    setSelectedStore("");
    setSelectedEdge("");
    setStores([]);
    setEdges([]);
    setFieldsData(null);
    setError(null);
    setLoading(false);
    setSelectAll(false);
    setSelectedShelves([]);
    navigate("?"); // Reset URL to the initial state without any parameters
  };

  // Toggle selection of a field
  const handleFieldToggle = (fieldId) => {
    setSelectedFields((prevSelected) =>
      prevSelected.includes(fieldId)
        ? prevSelected.filter((id) => id !== fieldId)
        : [...prevSelected, fieldId]
    );
  };

  // Toggle selection of a shelf
  const handleShelfToggle = (shelfSensorId) => {
    setSelectedShelves((prevSelected) =>
      prevSelected.includes(shelfSensorId)
        ? prevSelected.filter((id) => id !== shelfSensorId)
        : [...prevSelected, shelfSensorId]
    );
  };

  // Select all shelves
  const handleSelectAllShelves = () => {
    const allShelves = fieldsData.flatMap((field) =>
      field.shelves.map((shelf) => shelf.sensorId)
    );
    setSelectedShelves(selectAll ? [] : allShelves);
    setSelectAll(!selectAll);
  };

  // Select all shelves for a specific field
  const handleSelectAllShelvesForField = (fieldId) => {
    const field = fieldsData.find((field) => field._id === fieldId);
    if (field) {
      const fieldShelves = field.shelves.map((shelf) => shelf.sensorId);
      setSelectedShelves((prevSelected) =>
        prevSelected.includes(fieldShelves[0])
          ? prevSelected.filter((id) => !fieldShelves.includes(id))
          : [...prevSelected, ...fieldShelves]
      );
    }
  };

  // Send selected shelf IDs to the backend, for full reload
  const handleSendShelves = async () => {
    if (selectedShelves.length > 0) {
      try {
        const res = await api({
          type: "ReloadPlanogramsFromDB",
          data: {
            edgeId: selectedEdge,
            shelfIds: selectedShelves,
          },
        });
        alert("Shelf IDs sent successfully!");
      } catch (err) {
        console.error("Failed to send shelf IDs:", err);
        alert("Failed to send shelf IDs. Please try again later.");
      }
    } else {
      alert("Please select at least one shelf to send.");
    }
  };

  // Send selected shelf IDs to the backend, for applyPlanogram
  const handleSendShelvesForPartialPlanogam = async () => {
    if (selectedShelves.length > 0) {
      try {
        const res = await api({
          type: "applyPlanogram",
          data: {
            edgeId: selectedEdge,
            shelfIds: selectedShelves,
          },
        });
        alert("Shelf IDs sent successfully! (applyPlanogram)");
      } catch (err) {
        console.error("Failed to send shelf IDs in handleSendShelvesForApplyPlanogam:", err);
        alert("Failed to send shelf IDs in handleSendShelvesForApplyPlanogam. Please try again later.");
      }
    } else {
      alert("Please select at least one shelf to send. (handleSendShelvesForApplyPlanogam)");
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* AppBar */}

      {/* Main Container */}
      <div className="flex-1 overflow-y-auto bg-gray-100 py-8 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Select */}
          <div>
            <label htmlFor="client" className="block text-lg font-medium mb-2">
              Select Client
            </label>
            <select
              id="client"
              value={selectedClient}
              onChange={(e) => handleClientChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
            >
              <option value="">None</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Store Select */}
          <div>
            <label htmlFor="store" className="block text-lg font-medium mb-2">
              Select Store
            </label>
            <select
              id="store"
              value={selectedStore}
              onChange={(e) => handleStoreChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
              disabled={!selectedClient}
            >
              <option value="">None</option>
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          {/* Edge Select */}
          <div>
            <label htmlFor="edge" className="block text-lg font-medium mb-2">
              Select Edge
            </label>
            <select
              id="edge"
              value={selectedEdge}
              onChange={(e) => handleEdgeChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
              disabled={!selectedStore}
            >
              <option value="">None</option>
              {edges.map((edge) => (
                <option key={edge._id} value={edge._id}>
                  {edge.floorName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear All Button */}
        <div className="mt-4">
          <button
            onClick={handleClearAll}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Clear All Selections
          </button>
        </div>

        {/* Fields List */}
        <div className="mt-6">
          {loading && (
            <div className="flex flex-col justify-center items-center space-y-2">
              <div className="animate-spin rounded-full border-4 border-t-4 border-gray-500 w-16 h-16"></div>
              <p className="text-lg">Loading Fields...</p>
            </div>
          )}
          {error && (
            <div className="text-center text-red-500">
              <p className="text-lg">{error}</p>
            </div>
          )}
          {fieldsData && fieldsData.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllShelves}
                  className="mr-2"
                />
                <label className="text-lg font-medium">
                  Select All Shelves
                </label>
              </div>
              <div className="flex flex-wrap justify-around">
                {fieldsData.map((field) => (
                  <div
                    key={field._id}
                    className={`bg-white rounded-lg shadow-lg w-72 m-4   `}
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-xl font-semibold">{field.name}</h3>
                        <input
                          type="checkbox"
                          checked={selectedShelves.some((shelfId) =>
                            field.shelves.some((shelf) => shelf.sensorId === shelfId)
                          )}
                          onChange={() =>
                            handleSelectAllShelvesForField(field._id)
                          }
                          className="mr-2"
                        />
                      </div>
                      <h4 className="text-gray-600 font-semibold">
                        Shelves: <b>{field.shelves.length}</b>
                      </h4>
                      <p>
                        <ul>
                          {field.shelves.map((shelf) => (
                            <li key={shelf.sensorId} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedShelves.includes(shelf.sensorId)}
                                onChange={() => handleShelfToggle(shelf.sensorId)}
                                className="mr-2"
                              />
                              {shelf?.sensor?.boardId}{" "}
                              (<small style={{color:'gray'}}> {shelf.sensorId}</small>)
                            </li>
                          ))}
                        </ul>
                      </p>
                      <p className="text-gray-600">
                        Cameras: {field.cameras.length}
                      </p>
                    </div>
                    <div className="p-4 border-t"></div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                {/* Send Full Planogram Button */}
                <button
                  className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 ml-4"
                  onClick={handleSendShelves}
                >
                  Reload Full Planogram ({selectedShelves.length})
                </button>
                {/* Send Partial Planogram Button */}
                <button
                  className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-green-700 ml-4"
                  onClick={handleSendShelvesForPartialPlanogam}
                >
                  Apply Planogram ({selectedShelves.length})
                </button>
              </div>
            </div>
          )}
          {fieldsData && fieldsData.length === 0 && (
            <div className="text-center">
              <p className="text-lg">No Fields Available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReloadPlanograms;
